import { Component, OnInit, OnDestroy, Renderer2 } from '@angular/core';
import { AppService } from '../../utils/services/app.service';
import { FormGroup, Validators, FormBuilder } from '@angular/forms';
import { ApiService } from '../../utils/services/api.service';
import { Observable, of, Subject, Subscription, } from 'rxjs';
import { ACSService } from 'src/app/utils/services/azure/acs.service';
import { CasService } from 'src/app/utils/services/cas.service';
import { ADB2C_CODE, TOKEN } from 'src/app/utils/items/storage-names';
import { ActivatedRoute, Router } from '@angular/router';
import { DoctorService } from 'src/app/utils/services/doctor/doctor.service';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit, OnDestroy {
  authSubscription: Subscription;
  loginForm: FormGroup;
  user: any;
  processing = false;
  forgotPasswordModalVisible = false;
  private _unsubscribeAll: Subject<any> = new Subject<any>();
  isStaff: boolean = false;
  isDev: boolean = false;
  showPassword: boolean = false;

  constructor(
    private router: Router,
    private renderer: Renderer2,
    private _route: ActivatedRoute,
    private appService: AppService,
    private service: ApiService,
    private doctorService: DoctorService,
    private casService: CasService,
    private callService: ACSService,
    private fb: FormBuilder,
    private _toastr: ToastrService
  ) { this.adb2cLogin(); }

  ngOnInit() {
    this.renderer.addClass(document.body, 'login-container');

    this.loginForm = this.fb.group({
      email: [null, [Validators.required, Validators.email]],
      password: [null, Validators.required],
      remember_me: false
    });

    // Check if user just registered successfully
    this._route.queryParams
      .pipe(
        filter(params => params['registered'] === 'success'),
        take(1)
      )
      .subscribe(params => {
        this._toastr.success('Registration successful! Please login with your credentials.', 'Success', {
          timeOut: 5000,
        });
        // Clear the query parameter from URL
        this.router.navigate(['/login'], { replaceUrl: true });
      });
  }

  async logIn() {
    console.log('🔵 LOGIN FUNCTION CALLED');
    this.processing = true;

    this.service.authenticate(this.loginForm.value)
      .pipe(
        switchMap((response: any) => {
          this.user = response?.user;
          localStorage.setItem(TOKEN, response?.access_token);
          console.log('=== LOGIN: User after authentication ===');
          console.log('Full user object:', this.user);
          console.log('is_new_user:', this.user?.is_new_user);
          console.log('active_subscription:', this.user?.active_subscription);
          console.log('subscription_status_code:', this.user?.active_subscription?.subscription_status_code);
          return this.getDoctorByUser();
        }))
      .subscribe((response: any) => {
        this.user.doctor = response;
        console.log('=== LOGIN: User after doctor data loaded ===');
        console.log('Full user object:', this.user);
        console.log('is_new_user:', this.user?.is_new_user);
        console.log('active_subscription:', this.user?.active_subscription);
        console.log('subscription_status_code:', this.user?.active_subscription?.subscription_status_code);
        console.log('Menu should show?', +this.user?.is_new_user === 0 || this.user?.active_subscription?.subscription_status_code === 'active');
        this.appService.storeUser(this.user);
        this.appService.setUser(this.user);
        this.accountVerification();
      }, (err) => {
        this.processing = false;
        this.appService.logout();
      })
  }

  accountVerification() {
    if (this.user.account_status == 'active' &&
      this.user.is_validated) {
      this.router.navigate(['/account-verified']);
    } else {
      // if (this.user?.active_subscription?.subscription_status_code !== 'active') {
      //   if (['free', 'voided'].includes(this.user?.active_subscription?.payment_status_code)) {
      //     this.router.navigate(['/dashboard']);
      //   } else {
      //     this.router.navigate(['/payment']);
      //   }
      // } else {
      //   this.router.navigate(['/dashboard']);
      // }
      this.router.navigate(['/dashboard']);
    }
  }

  async getDoctor() {
    this.doctorService.getDoctorByUser(this.user?.id, { includes: 'hmo,hospital_affiliations.hospital_master,clinics.resource.services.service_item' })
      .subscribe((response: any) => {
        this.user.doctor = response;
        
        // Handle is_onelife logic
        this.handleIsOnelife(response);
        
        this.appService.storeUser(this.user);
        this.accountVerification();
        // this.updateCommunication();
      });
  }

  /**
   * Handle the is_onelife flag from doctor response
   * @param doctorResponse The response from getDoctor API call
   */
  private handleIsOnelife(doctorResponse: any): void {
    // Extract is_onelife value (default to false if not present)
    const isOnelife = doctorResponse?.is_onelife ?? false;
    
    // Store in localStorage
    localStorage.setItem('is_onelife', JSON.stringify(isOnelife));
    
    // Check if user has manually selected a company
    const userSelectedCompany = localStorage.getItem('userSelectedCompany');
    
    // If is_onelife is true and user hasn't manually selected a company, set default company to id: 2
    if (isOnelife && !userSelectedCompany) {
      // Find company with id: 2 from companies data
      this.setDefaultCompany(2);
    }
    // If is_onelife is false, the switch company will be hidden by default
    // (This is handled in the UI components based on the localStorage value)
  }

  /**
   * Set default company in localStorage
   * @param companyId The ID of the company to set as default
   */
  private setDefaultCompany(companyId: number): void {
    // In a real implementation, you would fetch the company data from the service
    // For now, we'll use the known 1Life Healthcare Inc. company data
    const defaultCompany = {
      id: companyId,
      name: "1Life Healthcare Inc.",
      code: "2000",
      logoIcon: "logo2_icon.png",
      logo: "logo2.png",
      styles: {
        "margin-top": "30px", 
        "margin-left": "15px"  
      }
    };
    
    // Save to localStorage
    localStorage.setItem('selectedCompany', JSON.stringify(defaultCompany));
  }

  async updateCommunication() {
    const credentials = await this.callService.generateToken();

    await this.casService.updateUser(this.user?.id, {
      communication_user_id: credentials?.user?.communicationUserId,
      communication_token: credentials?.token
    }).subscribe((response: any) => {

      this.user.communication_user_id = credentials?.user?.communicationUserId;
      this.user.communication_token = credentials?.token;
      this.appService.storeUser(this.user);
      this.router.navigate(['/dashboard']);
    }, (err) => {
      this.processing = false;
    });

  }

  /* loginWithGoogle(): void {
    this.socialAuthService.signIn(GoogleLoginProvider.PROVIDER_ID).then((user) => {
      this.socialService.login(user);
    });
  } */

  adb2cLogin() {
    of(localStorage.getItem(ADB2C_CODE))
      .pipe(
        filter(code => !!code),
        switchMap((code: string) => {
          localStorage.removeItem(ADB2C_CODE);
          this.processing = true;
          return this.service.authenticateAdb2c({ code: code });
        }),
        filter((response: any) => {
          if (response?.user != null) {
            return true;
          }

          this.processing = false;
          return false;
        }),
        switchMap((response: any) => {
          this.user = response?.user;
          localStorage.setItem(TOKEN, response?.access_token);
          return this.getDoctorByUser();
        }),
        take(1),
      )
      .subscribe((response: any) => {
        this.processing = false;
        this.user.doctor = response;
        this.appService.storeUser(this.user);
        this.accountVerification();
      }, err => {
        this.processing = false;
        this.appService.logout();
        console.log('PostAuth error:', err);

        this._toastr.error(err.error?.message, null, {
          timeOut: 1000,
        });
      });

    this._route.queryParams
      .pipe(
        filter(params => params['token'] != null),
        map(params => params['token']),
        switchMap((token: string) => {
          this.processing = true;
          this.appService.logout(false);
          return this.service.adb2cClaimsLogin(token);
        }),
        filter((response: any) => {
          if (response?.user != null) {
            return true;
          }

          this.processing = false;
          return false;
        }),
        switchMap((response: any) => {
          this.user = response?.user;
          localStorage.setItem(TOKEN, response?.access_token);
          return this.getDoctorByUser();
        }),
        take(1))
      .subscribe((response: any) => {
        this.processing = false;
        this.user.doctor = response;
        this.appService.storeUser(this.user);
        this.accountVerification();
      }, err => {
        this.processing = false;
        this.appService.logout();
        console.log('PostAuth error:', err);

        this._toastr.error(err.error?.message, null, {
          timeOut: 1000,
        });
      });
  }

  getDoctorByUser(): Observable<Object> {
    return this.doctorService.getDoctorByUser(this.user?.id, { includes: 'hmo,hospital_affiliations.hospital_master,clinics.resource.services.service_item' });
  }


  mwellLogin() {
    window.location.href = environment.adb2c_auth_url;
  }

  mwellSignUp(): void {
    window.location.href = environment.mwell_basic + '/onboard/signup';
  }

  navigateToSignUp(): void {
    this.router.navigate(['/sign-up']);
  }

  openForgotPasswordModal(): void {
    this.forgotPasswordModalVisible = true;
  }

  closeForgotPasswordModal(): void {
    this.forgotPasswordModalVisible = false;
  }

  loginWithStaff(): void {
    this.isStaff = !this.isStaff;
  }

  ngOnDestroy() {
    this.renderer.removeClass(document.body, 'login-container');
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  triggerShowPassword(){
    this.showPassword = !this.showPassword;
  }

  getPasswordType(){
    if (this.showPassword){
      return 'text';
    }
    else{
      return 'password';
    }
  }
}
